import "./styles.scss";
import BarMenu from "../BarMenu";
import { MdNotifications, MdCheckCircle, MdInfo, MdWarning, MdError, MdMoreVert, MdDelete } from "react-icons/md";
import { TbLoader } from "react-icons/tb";
import { useState, useEffect, useContext } from "react";
import api from "../../utils/api";
import { Context } from "../../context/Contex";

export default function Notifications() {
    const [notifications, setNotifications] = useState([]);
    const [filter, setFilter] = useState("all");
    const [menuOpen, setMenuOpen] = useState(null);
    const [loading, setLoading] = useState(true);
    const [unreadCount, setUnreadCount] = useState(0);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const { refreshUnreadCount } = useContext(Context);

    // Buscar notificações
    useEffect(() => {
        fetchNotifications();
    }, [filter, page]);

    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const response = await api.get('/notifications', {
                params: { filter, page, limit: 50 }
            });

            if (response.data.success) {
                setNotifications(response.data.notifications);
                setUnreadCount(response.data.unreadCount);
                setHasMore(response.data.pagination.page < response.data.pagination.pages);
            }
        } catch (error) {
            console.error('Erro ao buscar notificações:', error);
        } finally {
            setLoading(false);
        }
    };

    const getIcon = (type) => {
        switch (type) {
            case "success": return <MdCheckCircle />;
            case "warning": return <MdWarning />;
            case "error": return <MdError />;
            default: return <MdInfo />;
        }
    };

    const markAsRead = async (id) => {
        try {
            const response = await api.put(`/notifications/${id}`, { read: true });
            
            if (response.data.success) {
                setNotifications(notifications.map(n => 
                    n._id === id ? { ...n, read: true } : n
                ));
                setUnreadCount(prev => Math.max(0, prev - 1));
                refreshUnreadCount()
            }
        } catch (error) {
            console.error('Erro ao marcar como lida:', error);
        }
        setMenuOpen(null);
    };

    const deleteNotification = async (id) => {
        try {
            const response = await api.delete(`/notifications/${id}`);
            
            if (response.data.success) {
                const deletedNotif = notifications.find(n => n._id === id);
                setNotifications(notifications.filter(n => n._id !== id));
                
                if (deletedNotif && !deletedNotif.read) {
                    setUnreadCount(prev => Math.max(0, prev - 1));
                }
                refreshUnreadCount()
            }
        } catch (error) {
            console.error('Erro ao excluir notificação:', error);
        }
        setMenuOpen(null);
    };

    const markAllAsRead = async () => {
        try {
            const response = await api.put('/notifications/mark-all-read');
            
            if (response.data.success) {
                // Atualizar todas as notificações para read: true
                setNotifications(prevNotifications => 
                    prevNotifications.map(n => ({ ...n, read: true }))
                );
                setUnreadCount(0);
                
                refreshUnreadCount()
                // Recarregar notificações para garantir sincronização
                await fetchNotifications();
            }
        } catch (error) {
            console.error('Erro ao marcar todas como lidas:', error);
        }
    };

    const deleteAllRead = async () => { 
        try {
            const response = await api.delete('/notifications?readOnly=true');
            
            if (response.data.success) {
                setNotifications(notifications.filter(n => !n.read));
                refreshUnreadCount()
            }
        } catch (error) {
            console.error('Erro ao excluir notificações:', error);
        }
    };

    const filteredNotifications = notifications.filter(n => {
        if (filter === "unread") return !n.read;
        if (filter === "read") return n.read;
        return true;
    });

    return (
        <div id="Notifications">
            <BarMenu />
            <section className="dashboard-section">
                <div className="notifications-container">
                    <div className="notifications-header">
                        <div className="header-top">
                            <div className="title-group">
                                <MdNotifications className="header-icon" />
                                <h1>Notificações</h1>
                                {unreadCount > 0 && (
                                    <span className="unread-badge">{unreadCount}</span>
                                )}
                            </div>
                            <div className="header-actions">
                                {unreadCount > 0 && (
                                    <button className="mark-all-btn" onClick={markAllAsRead}>
                                        Marcar todas como lidas
                                    </button>
                                )}
                                {notifications.some(n => n.read) && (
                                    <button className="delete-all-btn" onClick={deleteAllRead}>
                                        Limpar lidas
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="filters">
                            <button 
                                className={`filter-btn ${filter === "all" ? "active" : ""}`}
                                onClick={() => { setFilter("all"); setPage(1); }}
                            >
                                Todas ({notifications.length})
                            </button>
                            <button 
                                className={`filter-btn ${filter === "unread" ? "active" : ""}`}
                                onClick={() => { setFilter("unread"); setPage(1); }}
                            >
                                Não lidas ({unreadCount})
                            </button>
                            <button 
                                className={`filter-btn ${filter === "read" ? "active" : ""}`}
                                onClick={() => { setFilter("read"); setPage(1); }}
                            >
                                Lidas ({notifications.filter(n => n.read).length})
                            </button>
                        </div>
                    </div>

                    <div className="notifications-list">
                        {loading ? (
                            <div className="loading-state">
                                <div className="spinner"></div>
                                <p>Carregando notificações...</p>
                            </div>
                        ) : filteredNotifications.length === 0 ? (
                            <div className="empty-state">
                                <MdNotifications />
                                <h3>Nenhuma notificação</h3>
                                <p>Você não tem notificações {filter === "unread" ? "não lidas" : filter === "read" ? "lidas" : ""} no momento.</p>
                            </div>
                        ) : (
                            filteredNotifications.map(notification => (
                                <div 
                                    key={notification._id} 
                                    className={`notification-item ${notification.type} ${notification.read ? "read" : "unread"}`}
                                >
                                    <div className="notification-icon">
                                        {getIcon(notification.type)}
                                    </div>
                                    <div className="notification-content">
                                        <div className="notification-header-row">
                                            <h3>{notification.title}</h3>
                                            <div className="notification-actions">
                                                <span className="notification-time">{notification.time}</span>
                                                <button 
                                                    className="menu-btn"
                                                    onClick={() => setMenuOpen(menuOpen === notification._id ? null : notification._id)}
                                                >
                                                    <MdMoreVert />
                                                </button>
                                                {menuOpen === notification._id && (
                                                    <div className="dropdown-menu">
                                                        {!notification.read && (
                                                            <button onClick={() => markAsRead(notification._id)}>
                                                                <MdCheckCircle />
                                                                Marcar como lida
                                                            </button>
                                                        )}
                                                        <button onClick={() => deleteNotification(notification._id)}>
                                                            <MdDelete />
                                                            Excluir
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <p>{notification.message}</p>
                                    </div>
                                    {!notification.read && <div className="unread-indicator"></div>}
                                </div>
                            ))
                        )}
                    </div>

                    {hasMore && !loading && (
                        <div className="load-more">
                            <button onClick={() => setPage(prev => prev + 1)}>
                                Carregar mais
                            </button>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}